// src/pages/xray/XrayLobby/TabDHCP.js
import React, { useState, useMemo } from 'react';

const TabDHCP = ({
  selectedTabsLeasesDHCP,
  setSelectedTabsLeasesDHCP,
  scopesDHCPLimits,
  formattedDhcpLeases,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredCell, setHoveredCell] = useState(null);
  const [copiedCell, setCopiedCell] = useState(null);

  const handleCopyToClipboard = (text, cellId) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedCell(cellId);
      setTimeout(() => setCopiedCell(null), 1500);
    });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedLeases = useMemo(() => {
    if (!formattedDhcpLeases) {
      return formattedDhcpLeases;
    }

    // First, filter by search term
    let filtered = formattedDhcpLeases;
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = formattedDhcpLeases.filter((lease) => {
        return (
          (lease.ip || '').toLowerCase().includes(lowerSearch) ||
          (lease.mac || '').toLowerCase().includes(lowerSearch) ||
          (lease.hostname || '').toLowerCase().includes(lowerSearch) ||
          (lease.vendor || '').toLowerCase().includes(lowerSearch) ||
          (lease.model || '').toLowerCase().includes(lowerSearch)
        );
      });
    }

    // Then, sort if a column is selected
    if (!sortConfig.key) {
      return filtered;
    }

    const sorted = [...filtered].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';

      // Handle IP address sorting specially
      if (sortConfig.key === 'ip') {
        const aNum = aValue.split('.').map(num => num.padStart(3, '0')).join('');
        const bNum = bValue.split('.').map(num => num.padStart(3, '0')).join('');
        return sortConfig.direction === 'asc'
          ? aNum.localeCompare(bNum)
          : bNum.localeCompare(aNum);
      }

      // String comparison for other fields
      return sortConfig.direction === 'asc'
        ? aValue.toString().localeCompare(bValue.toString())
        : bValue.toString().localeCompare(aValue.toString());
    });

    return sorted;
  }, [formattedDhcpLeases, sortConfig, searchTerm]);

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span className="ml-1 text-gray-600 text-lg">▲▼</span>;
    }
    return sortConfig.direction === 'asc' ?
      <span className="ml-1 text-lg">▲</span> :
      <span className="ml-1 text-lg">▼</span>;
  };

  return (
    <>
      <div className="tabs mt-6 flex border-b">
        {['DHCP SCOPES', 'DHCP LEASES'].map((label, index) => (
          <label
            key={index}
            className={`cursor-pointer px-2 py-1 border-t border-r border-l border-red-600 bg-red-300 ml-1 rounded-t-md ${
              selectedTabsLeasesDHCP === `tabsLeasesDHCP${index + 1}`
                ? 'border-b-0 font-bold'
                : 'border-red-600 bg-red-600 hover:bg-red-700 transition-colors duration-200 text-white'
            }`}
          >
            <input
              type="radio"
              className="hidden"
              name="tabs-group"
              checked={selectedTabsLeasesDHCP === `tabsLeasesDHCP${index + 1}`}
              onChange={() =>
                setSelectedTabsLeasesDHCP(`tabsLeasesDHCP${index + 1}`)
              }
            />
            {label}
          </label>
        ))}
      </div>

      <div className="px-6 py-2 bg-white shadow-lg rounded-lg">
        {selectedTabsLeasesDHCP === 'tabsLeasesDHCP1' && (
          <div>
            <h1 className="font-bold text-2xl p-2">Defined DHCP scopes</h1>
            <pre className="bg-gray-100 p-4 rounded-md border">
              {scopesDHCPLimits || 'No DHCP Definitions available'}
            </pre>
          </div>
        )}

        {selectedTabsLeasesDHCP === 'tabsLeasesDHCP2' && (
          <div>
            <h1 className="font-bold text-2xl p-2">DHCP Leases table</h1>
            {formattedDhcpLeases?.length ? (
              <>
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search by IP, MAC, Hostname, Vendor, or Model..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                  {searchTerm && (
                    <p className="text-sm text-gray-600 mt-2">
                      Found {filteredAndSortedLeases.length} of {formattedDhcpLeases.length} leases
                    </p>
                  )}
                </div>
                <div className="overflow-x-auto">
                <table className="min-w-full table-auto border-collapse border border-red-300 text-sm">
                  <thead>
                    <tr className="bg-red-400 text-left">
                      <th
                        className="border border-black px-2 py-1 cursor-pointer hover:bg-red-500 select-none"
                        onClick={() => handleSort('ip')}
                      >
                        IP<SortIcon columnKey="ip" />
                      </th>
                      <th
                        className="border border-black px-2 py-1 cursor-pointer hover:bg-red-500 select-none"
                        onClick={() => handleSort('mac')}
                      >
                        MAC<SortIcon columnKey="mac" />
                      </th>
                      <th className="border border-black px-2 py-1 bg-red-400">
                        MAC no colon
                      </th>
                      <th
                        className="border border-black px-2 py-1 cursor-pointer hover:bg-red-500 select-none"
                        onClick={() => handleSort('hostname')}
                      >
                        Client Hostname<SortIcon columnKey="hostname" />
                      </th>
                      <th
                        className="border border-black px-2 py-1 cursor-pointer hover:bg-red-500 select-none"
                        onClick={() => handleSort('vendor')}
                      >
                        Vendor<SortIcon columnKey="vendor" />
                      </th>
                      <th
                        className="border border-black px-2 py-1 cursor-pointer hover:bg-red-500 select-none"
                        onClick={() => handleSort('model')}
                      >
                        Model<SortIcon columnKey="model" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedLeases.map((lease, index) => {
                      const macNoColon = lease.mac ? lease.mac.replace(/:/g, '') : '';
                      return (
                        <tr key={index} className="hover:bg-red-200">
                          <td
                            className="border border-black px-2 py-1 relative"
                            onMouseEnter={() => setHoveredCell(`${index}-ip`)}
                            onMouseLeave={() => setHoveredCell(null)}
                          >
                            {lease.ip}
                            {hoveredCell === `${index}-ip` && (
                              <button
                                onClick={() => handleCopyToClipboard(lease.ip, `${index}-ip`)}
                                className="absolute right-1 top-1/2 -translate-y-1/2 hover:opacity-70 p-0.5"
                                title="Copy to clipboard"
                              >
                                {copiedCell === `${index}-ip` ? (
                                  <span className="text-green-600 text-sm font-bold">✓</span>
                                ) : (
                                  <img src="/img/svg-copy-to-clipboard.svg" alt="Copy" className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </td>
                          <td
                            className="border border-black px-2 py-1 relative"
                            onMouseEnter={() => setHoveredCell(`${index}-mac`)}
                            onMouseLeave={() => setHoveredCell(null)}
                          >
                            {lease.mac}
                            {hoveredCell === `${index}-mac` && (
                              <button
                                onClick={() => handleCopyToClipboard(lease.mac, `${index}-mac`)}
                                className="absolute right-1 top-1/2 -translate-y-1/2 hover:opacity-70 p-0.5"
                                title="Copy to clipboard"
                              >
                                {copiedCell === `${index}-mac` ? (
                                  <span className="text-green-600 text-sm font-bold">✓</span>
                                ) : (
                                  <img src="/img/svg-copy-to-clipboard.svg" alt="Copy" className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </td>
                          <td
                            className="border border-black px-2 py-1 relative"
                            onMouseEnter={() => setHoveredCell(`${index}-macNoColon`)}
                            onMouseLeave={() => setHoveredCell(null)}
                          >
                            {macNoColon}
                            {hoveredCell === `${index}-macNoColon` && (
                              <button
                                onClick={() => handleCopyToClipboard(macNoColon, `${index}-macNoColon`)}
                                className="absolute right-1 top-1/2 -translate-y-1/2 hover:opacity-70 p-0.5"
                                title="Copy to clipboard"
                              >
                                {copiedCell === `${index}-macNoColon` ? (
                                  <span className="text-green-600 text-sm font-bold">✓</span>
                                ) : (
                                  <img src="/img/svg-copy-to-clipboard.svg" alt="Copy" className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </td>
                          <td
                            className="border border-black px-2 py-1 relative"
                            onMouseEnter={() => setHoveredCell(`${index}-hostname`)}
                            onMouseLeave={() => setHoveredCell(null)}
                          >
                            {lease.hostname}
                            {hoveredCell === `${index}-hostname` && (
                              <button
                                onClick={() => handleCopyToClipboard(lease.hostname, `${index}-hostname`)}
                                className="absolute right-1 top-1/2 -translate-y-1/2 hover:opacity-70 p-0.5"
                                title="Copy to clipboard"
                              >
                                {copiedCell === `${index}-hostname` ? (
                                  <span className="text-green-600 text-sm font-bold">✓</span>
                                ) : (
                                  <img src="/img/svg-copy-to-clipboard.svg" alt="Copy" className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </td>
                          <td
                            className="border border-black px-2 py-1 relative"
                            onMouseEnter={() => setHoveredCell(`${index}-vendor`)}
                            onMouseLeave={() => setHoveredCell(null)}
                          >
                            {lease.vendor}
                            {hoveredCell === `${index}-vendor` && (
                              <button
                                onClick={() => handleCopyToClipboard(lease.vendor, `${index}-vendor`)}
                                className="absolute right-1 top-1/2 -translate-y-1/2 hover:opacity-70 p-0.5"
                                title="Copy to clipboard"
                              >
                                {copiedCell === `${index}-vendor` ? (
                                  <span className="text-green-600 text-sm font-bold">✓</span>
                                ) : (
                                  <img src="/img/svg-copy-to-clipboard.svg" alt="Copy" className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </td>
                          <td
                            className="border border-black px-2 py-1 relative"
                            onMouseEnter={() => setHoveredCell(`${index}-model`)}
                            onMouseLeave={() => setHoveredCell(null)}
                          >
                            {lease.model}
                            {hoveredCell === `${index}-model` && (
                              <button
                                onClick={() => handleCopyToClipboard(lease.model, `${index}-model`)}
                                className="absolute right-1 top-1/2 -translate-y-1/2 hover:opacity-70 p-0.5"
                                title="Copy to clipboard"
                              >
                                {copiedCell === `${index}-model` ? (
                                  <span className="text-green-600 text-sm font-bold">✓</span>
                                ) : (
                                  <img src="/img/svg-copy-to-clipboard.svg" alt="Copy" className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            ) : (
              <pre className="bg-gray-100 p-4 rounded-md border">
                No DHCP Leases data available
              </pre>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default TabDHCP;
